// Define mapping between keys and section/subsection paths
import {
  BusinessPlanSurvey,
  BusinessPlanSurveySectionGuidanceKey,
  chatGptContextDependencyMap,
  examplesMap,
  instructionsMap,
  sectionsMap,
  SurveySectionGuidance,
} from './types';
import { MARKDOWN_SEPARATOR } from '../growegy-ai/types'; // Generic prompt generation function

const generateMaxMinWordsLimit = (min: number | null, max: number | null) => {
  if (max && min) {
    return `The content should have from ${min} to ${max} words.`;
  } else if (max) {
    return `The content should have at most ${max} words.`;
  } else if (min) {
    return `The content should have at least ${min} words.`;
  }
  return 'The content should have approximately the same amount of words as the examples provided';
};

// Generic prompt generation function
export function generateSurveySectionGuidance(
  key: BusinessPlanSurveySectionGuidanceKey,
  context: {
    companyName: string;
    companyDescription: string;
    productOrService: string[];
    targetAudience: string[];
    incorporationType: string | undefined;
    incorporationState: string | undefined;
    fundingNeed: string | undefined;
    principalMembers: string[];
  },
  draftToBeImproved: string,
  businessPlan: BusinessPlanSurvey
): SurveySectionGuidance {
  const { publicContent: publicInstruction, hiddenContent: hiddenInstruction } =
    instructionsMap[key];
  const examples = examplesMap[key].map((x) => x.content);
  const sections = sectionsMap[key];
  const sectionPath = `'${sections.section}: ${sections.subsection}'`;
  const maxMinWordsLimit = generateMaxMinWordsLimit(sections.wordsMin, sections.wordsMax);

  const requiredSections = chatGptContextDependencyMap[key] || [];

  const previousSectionsText = businessPlan.sections
    .filter((section) => requiredSections.includes(section.key) && !!section.value)
    .map((section) => {
      const header = `"${sectionsMap[section.key].section}" -> "${
        sectionsMap[section.key].subsection
      }"`;
      return (
        `\n----START--${header}----\n` +
        `${section.value.length >= 1500 ? section.value.slice(0, 1500) + '...' : section.value}` +
        `\n----END--${header}----\n`
      );
    })
    .join('\n');

  const previousSectionsFullText = previousSectionsText
    ? `

Relevant Business Plan Sections:${previousSectionsText}`
    : '';

  // Template for the prompt
  const principalMembers = context.principalMembers
    ? `- Principal members:
${context.principalMembers.map((x) => `  - ${x}`).join('\n')}`
    : '';
  const fundingNeed = context.fundingNeed ? `- Funding need: ${context.fundingNeed}` : '';
  const incorporationState = context.incorporationState
    ? `- Company incorporation state: ${context.incorporationState}`
    : '';
  const incorporationType = context.incorporationType
    ? `- Company incorporation type: ${context.incorporationType}`
    : '';
  const gptSystemMessage = `You are assisting in generating a business plan for a small business.
The user is currently working on the ${sectionPath} section of the business plan.
Here is the general instruction for this section:
===
${publicInstruction}
===

${
  hiddenInstruction
    ? `Here are some important notes:
===
${hiddenInstruction}
===

`
    : ''
}
${examples.length > 0 ? `Here is an example for the ${sectionPath} section: ${examples[0]}` : ''}

When asked to write the content of the section:
1. ${maxMinWordsLimit}
2. You should focus on creating a compelling and relevant description for this section, incorporating any relevant strategic insights and values.
3. The content should be straightforward and professionally written, suitable for an business plan that may be used to attract funding.

Company information:
- Company Name: "${context.companyName}"
- Company Description: ${context.companyDescription}
- Company incorporation country: US
${incorporationState}
${incorporationType}
- Company Product or service: ${context.productOrService.join(', ')}
- Target Audience: ${context.targetAudience.join(', ')}
${fundingNeed}
${principalMembers} 
${previousSectionsFullText}

IMPORTANT! Do not fabricate facts such as people's names, job titles, addresses, the company's legal structure, trademarks, website domains, the amounts of money like those requested for funding, duration of the loan, etc. If certain information is unavailable, request the needed details from the user before proceeding.
IMPORTANT! Always use the "update_business_plan_section" function call to generate content for a business plan section, do not return it as text response.`;

  const draftTextInstruction = draftToBeImproved
    ? `\n\nPlease take the existing ${sectionPath} text provided into consideration. ` +
      'Use it as a foundation, expanding and refining as necessary to create a complete, ' +
      `professional ${sectionPath} section:` +
      `\n${MARKDOWN_SEPARATOR}\n${draftToBeImproved}`
    : '';
  // Template for the prompt
  const prompt = `Please generate the ${sectionPath} section.` + draftTextInstruction;

  return {
    key,
    instruction: publicInstruction,
    examples,
    chatGptGenerateTemplateSystemMessage: gptSystemMessage,
    chatGptGenerateTemplatePrompt: prompt,
  };
}
